import 'bootstrap/dist/css/bootstrap.min.css';
import { useEffect, useRef, useState } from 'react';
import './Testimonials.css';
import testimonials from '../../data/testimonials/testimonialsData.json';
import ResponsiveImage from '../../components/ResponsiveImage';

const wrapIndex = index => ((index % testimonials.length) + testimonials.length) % testimonials.length;
const portraitOffset = (index, activeIndex) => {
    const offset = wrapIndex(index - activeIndex);
    return offset > testimonials.length / 2 ? offset - testimonials.length : offset;
};

export default function Testimonials() {
    const [activeIndex, setActiveIndex] = useState(0);
    const [expanded, setExpanded] = useState({});
    const [hasInteracted, setHasInteracted] = useState(false);
    const [hoveredArea, setHoveredArea] = useState(null);
    const [hasKeyboardFocus, setHasKeyboardFocus] = useState(false);
    const keyboardNavigation = useRef(false);
    const [reducedMotion, setReducedMotion] = useState(() =>
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );

    const selectIndex = next => {
        setHasInteracted(true);
        setActiveIndex(current => wrapIndex(typeof next === 'function' ? next(current) : next));
    };

    useEffect(() => {
        const media = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => setReducedMotion(media.matches);
        media.addEventListener('change', update);
        return () => media.removeEventListener('change', update);
    }, []);

    useEffect(() => {
        const onKeyDown = event => {
            if (event.key === 'Tab') keyboardNavigation.current = true;
        };
        const onPointerDown = () => {
            keyboardNavigation.current = false;
            setHasKeyboardFocus(false);
        };
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('pointerdown', onPointerDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('pointerdown', onPointerDown);
        };
    }, []);

    useEffect(() => {
        if (hoveredArea || hasKeyboardFocus || reducedMotion) return;
        const timer = window.setTimeout(() => {
            setHasInteracted(false);
            setActiveIndex(current => wrapIndex(current + 1));
        }, 4000);
        return () => window.clearTimeout(timer);
    }, [activeIndex, hoveredArea, hasKeyboardFocus, reducedMotion]);

    const pauseOnHover = area => ({
        onPointerEnter: event => {
            if (event.pointerType === 'mouse') setHoveredArea(area);
        },
        onPointerLeave: () => setHoveredArea(current => current === area ? null : current)
    });

    const person = testimonials[activeIndex];
    const paragraphs = person.message.split(/\n\s*\n/).map(paragraph => paragraph.trim()).filter(Boolean);
    const isLong = paragraphs.length > 1;

    return <section
        id="testimonials"
        className="rg-testimonials"
        aria-labelledby="testimonials-heading"
        onFocusCapture={() => setHasKeyboardFocus(keyboardNavigation.current)}
        onBlurCapture={event => {
            if (!event.currentTarget.contains(event.relatedTarget)) setHasKeyboardFocus(false);
        }}
    >
        <div className="wrapper">
            <header className="rg-testimonials-heading">
                <p className="rg-testimonials-eyebrow">KIND WORDS</p>
                <h2 id="testimonials-heading">What clients say.</h2>
                <p>A few words from the photographers I work with.</p>
            </header>

            <div className="rg-testimonial-navigation">
                <button className="rg-testimonial-arrow rg-testimonial-prev" type="button" aria-label="Previous testimonial" onClick={() => selectIndex(current => current - 1)}>
                    <i className="fas fa-chevron-left" aria-hidden="true"></i>
                </button>

                <div className="rg-testimonial-avatars" role="group" aria-label="Choose a client testimonial" {...pauseOnHover('portraits')}>
                    {testimonials.map((person, index) => {
                        const offset = portraitOffset(index, activeIndex);
                        const visible = Math.abs(offset) <= 2;
                        return <button
                            key={person.name}
                            type="button"
                            className={`rg-testimonial-avatar${index === activeIndex ? ' is-active' : ''}${visible ? '' : ' is-outside'}`}
                            style={{ '--avatar-offset': offset }}
                            onClick={() => selectIndex(index)}
                            aria-label={`Read testimonial from ${person.name.trim()}`}
                            aria-pressed={index === activeIndex}
                            aria-hidden={!visible}
                            tabIndex={visible ? 0 : -1}
                        >
                            <ResponsiveImage src={person.avatar.startsWith('/') ? person.avatar : '/' + person.avatar} alt={person.name.trim()} sizes="70px" loading="lazy" />
                        </button>;
                    })}
                </div>

                <button className="rg-testimonial-arrow rg-testimonial-next" type="button" aria-label="Next testimonial" onClick={() => selectIndex(current => current + 1)}>
                    <i className="fas fa-chevron-right" aria-hidden="true"></i>
                </button>
            </div>

            <div
                className="rg-testimonial-stage"
                {...pauseOnHover('quote')}
                aria-live={hasInteracted ? 'polite' : 'off'}
                aria-atomic="true"
            >
                <article key={person.name} className="rg-testimonial-panel" aria-labelledby={`testimonial-name-${activeIndex}`}>
                    <header>
                        <h3 id={`testimonial-name-${activeIndex}`}>{person.name.trim()}</h3>
                        {person.subName && <p className="rg-testimonial-studio">{person.subName}</p>}
                    </header>
                    <div className="rg-testimonial-quote">
                        <i className="fas fa-quote-left" aria-hidden="true"></i>
                        <div>
                            <blockquote id={`testimonial-message-${activeIndex}`}>
                                {(expanded[activeIndex] ? paragraphs : paragraphs.slice(0, 1)).map((paragraph, paragraphIndex) => <p key={paragraphIndex}>{paragraph}</p>)}
                            </blockquote>
                            {isLong && <button
                                type="button"
                                className="rg-testimonial-more"
                                aria-expanded={Boolean(expanded[activeIndex])}
                                aria-controls={`testimonial-message-${activeIndex}`}
                                onClick={() => {
                                    setHasInteracted(true);
                                    setExpanded(current => ({ ...current, [activeIndex]: !current[activeIndex] }));
                                }}
                            >{expanded[activeIndex] ? 'Show less' : 'Show more'}</button>}
                        </div>
                    </div>
                </article>
            </div>
        </div>
    </section>;
}
